import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Leaf, Fish, PiggyBank, TreePalm, Carrot, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1671208615878-c074b4bf08dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxsdXNoJTIwdmVnZXRhYmxlJTIwZmFybSUyMG5pZ2VyaWElMjBwYWxtJTIwb2lsJTIwdHJlZSUyMHBsYW50YXRpb24lMjBhZnJpY2FuJTIwZmFybWVyJTIwaGFydmVzdCUyMGZyZXNoJTIwdmVnZXRhYmxlcyUyMG1hcmtldHxlbnwwfHx8fDE3NzI4MzM3NDF8MA&ixlib=rb-4.1.0&q=85";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_igbodu-agri/artifacts/swcybtk6_WhatsApp%20Image%202026-03-03%20at%2012.01.34%20PM.jpeg";

const services = [
  {
    icon: PiggyBank,
    title: "Pig Farming",
    description: "Quality pigs raised with modern techniques and proper nutrition.",
    image: "https://customer-assets.emergentagent.com/job_igbodu-agri/artifacts/54tu1wv0_pig-photo111.jpg"
  },
  {
    icon: Fish,
    title: "Fish Farming",
    description: "Fresh catfish and tilapia from our sustainable aquaculture ponds.",
    image: "https://customer-assets.emergentagent.com/job_igbodu-agri/artifacts/wnfypbzf_WhatsApp%20Image%202026-04-01%20at%2010.59.50%20AM.jpeg"
  },
  {
    icon: Leaf,
    title: "Grasscutter",
    description: "Locally bred grasscutter, a Nigerian delicacy in high demand.",
    image: "https://customer-assets.emergentagent.com/job_igbodu-agri/artifacts/54lc9buf_image.png"
  },
  {
    icon: Carrot,
    title: "Vegetables",
    description: "Fresh, organic vegetables grown using sustainable methods.",
    image: "https://customer-assets.emergentagent.com/job_igbodu-agri/artifacts/2mv30pjo_The-Secret-History-of-Okra-1.jpg"
  },
  {
    icon: TreePalm,
    title: "Palm Trees",
    description: "Palm oil and palm kernel products from our plantation.",
    image: "https://images.unsplash.com/photo-1671208615878-c074b4bf08dc?w=600"
  }
];

const stats = [
  { value: "10+", label: "Years Experience" },
  { value: "500+", label: "Happy Customers" },
  { value: "5", label: "Farm Sections" },
  { value: "100%", label: "Organic Produce" }
];

export default function HomePage() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products`).then(res => setProducts(res.data.slice(0, 3))).catch(console.error);
  }, []);

  return (
    <div className="pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl animate-fade-up">
            <span className="inline-block font-accent text-sm uppercase tracking-widest text-gold mb-6">
              Welcome to AfriGrow Farm
            </span>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white leading-none mb-6">
              Growing Nigeria's <span className="text-gold">Future</span>
            </h1>
            <p className="text-lg md:text-xl text-stone-300 leading-relaxed mb-10 max-w-xl">
              Premium agricultural production and livestock farming in Igbodu, Lagos. From our farm to your table, we deliver quality.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                asChild 
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white rounded-full px-8 py-6 text-lg btn-primary"
                data-testid="hero-explore-btn"
              >
                <Link to="/products">
                  Explore Products <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button 
                asChild 
                variant="outline" 
                size="lg"
                className="border-white text-white hover:bg-white hover:text-stone-900 rounded-full px-8 py-6 text-lg"
                data-testid="hero-contact-btn"
              >
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-green-600 py-12" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="font-heading text-4xl md:text-5xl font-bold text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-green-100 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="section-padding bg-stone-50" data-testid="about-preview-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={LOGO_URL}
                  alt="AfriGrow Farm" 
                  className="w-full h-[500px] object-contain bg-white p-8"
                />
              </div>
              <div className="absolute -bottom-8 -right-8 bg-terracotta text-white p-6 rounded-2xl shadow-xl hidden md:block">
                <div className="font-heading text-3xl font-bold">10+</div>
                <div className="text-sm">Years of Excellence</div>
              </div>
            </div>
            
            <div>
              <span className="font-accent text-sm uppercase tracking-widest text-terracotta mb-4 block">
                About AfriGrow Farm
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-stone-900 mb-6">
                Sustainable Farming for a Better Tomorrow
              </h2>
              <p className="text-stone-600 text-lg leading-relaxed mb-8">
                Located in the heart of Igbodu, Lagos, AfriGrow Farm has been at the forefront of sustainable agriculture and livestock production. We combine traditional farming wisdom with modern techniques to deliver the finest produce to our customers.
              </p>
              
              <ul className="space-y-4 mb-8">
                {["100% Organic produce", "Ethical animal husbandry", "Fresh daily harvests", "Direct farm-to-table delivery"].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                    <span className="text-stone-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                asChild
                className="bg-green-600 hover:bg-green-700 text-white rounded-full px-8 py-6 text-lg"
                data-testid="learn-more-btn"
              >
                <Link to="/about">
                  Learn More <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Bento Grid */}
      <section className="section-padding bg-white" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="font-accent text-sm uppercase tracking-widest text-terracotta mb-4 block">
              What We Offer
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-stone-900 mb-4">
              Our Farm Services
            </h2>
            <p className="text-stone-600 text-lg">
              From livestock to crops, we provide a diverse range of quality agricultural products.
            </p>
          </div>
          
          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Large Card - Pig Farming */}
            <div className="md:col-span-7 group relative overflow-hidden rounded-2xl h-[400px] card-hover" data-testid="service-pig">
              <img 
                src={services[0].image} 
                alt={services[0].title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-3 bg-green-600 rounded-xl">
                    <PiggyBank className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-white">{services[0].title}</h3>
                </div>
                <p className="text-stone-300 max-w-md">{services[0].description}</p>
              </div>
            </div>
            
            {/* Small Card - Fish Farming */}
            <div className="md:col-span-5 group relative overflow-hidden rounded-2xl h-[400px] card-hover" data-testid="service-fish">
              <img 
                src={services[1].image} 
                alt={services[1].title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-3 bg-terracotta rounded-xl">
                    <Fish className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-white">{services[1].title}</h3>
                </div>
                <p className="text-stone-300">{services[1].description}</p>
              </div>
            </div>
            
            {/* Three equal cards */}
            {services.slice(2).map((service) => {
              const IconComponent = service.icon;
              return (
                <div 
                  key={service.title}
                  className="md:col-span-4 group relative overflow-hidden rounded-2xl h-[300px] card-hover" 
                  data-testid={`service-${service.title.toLowerCase().replace(' ', '-')}`}
                >
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-gold rounded-lg">
                        <IconComponent className="h-5 w-5 text-stone-900" />
                      </div>
                      <h3 className="font-heading text-xl font-semibold text-white">{service.title}</h3>
                    </div>
                    <p className="text-stone-300 text-sm">{service.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="text-center mt-12">
            <Button 
              asChild
              variant="outline"
              className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white rounded-full px-8 py-6 text-lg"
              data-testid="view-services-btn"
            >
              <Link to="/services">
                View All Services <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {products.length > 0 && (
        <section className="section-padding bg-stone-100" data-testid="products-section">
          <div className="container-custom">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="font-accent text-sm uppercase tracking-widest text-terracotta mb-4 block">
                Fresh From The Farm
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-stone-900 mb-4">
                Our Products
              </h2>
              <p className="text-stone-600 text-lg">
                Quality products directly from AfriGrow Farm to your table.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {products.map((product) => (
                <div 
                  key={product.id} 
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow card-hover"
                  data-testid={`product-${product.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src={product.image_url || "https://images.unsplash.com/photo-1585540083814-ea6ee8af9e4f?w=600"} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <span className="font-accent text-xs uppercase tracking-widest text-terracotta">
                      {product.category}
                    </span>
                    <h3 className="font-heading text-xl font-semibold text-stone-900 mt-2 mb-2">
                      {product.name}
                    </h3>
                    <p className="text-stone-600 text-sm mb-4 line-clamp-2">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${product.available ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}`}>
                        {product.available ? 'Available' : 'Out of Stock'}
                      </span>
                      <Button 
                        asChild
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                      >
                        <Link to="/contact">Get Quote</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-12">
              <Button 
                asChild
                className="bg-green-600 hover:bg-green-700 text-white rounded-full px-8 py-6 text-lg"
                data-testid="view-products-btn"
              >
                <Link to="/products">
                  View All Products <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="section-padding bg-green-600 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <pattern id="pattern" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
              <circle cx="5" cy="5" r="1" fill="currentColor" />
            </pattern>
            <rect x="0" y="0" width="100" height="100" fill="url(#pattern)" />
          </svg>
        </div>
        
        <div className="container-custom relative z-10 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Order Fresh Farm Produce?
          </h2>
          <p className="text-green-100 text-lg md:text-xl max-w-2xl mx-auto mb-10">
            Contact us today for bulk orders, farm visits, or any inquiries. We're here to serve you.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              asChild 
              size="lg"
              className="bg-white text-green-700 hover:bg-stone-100 rounded-full px-8 py-6 text-lg font-semibold"
              data-testid="cta-contact-btn"
            >
              <Link to="/contact">Get in Touch</Link>
            </Button>
            <Button 
              asChild 
              variant="outline" 
              size="lg"
              className="border-white text-white hover:bg-white hover:text-green-700 rounded-full px-8 py-6 text-lg"
              data-testid="cta-phone-btn"
            >
              <a href="tel:+2348026994903">Call: +234 802 699 4903</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
